<template>
  <DashboardCard
    :expanded="expanded"
    :loading="loading"
    :widget-type="widgetType"
    title="Critical Dates"
    :count="totalRows"
    @update:expanded="$emit('update:expanded', $event)"
  >
    <template #actions>
      <v-menu
        v-if="$vuetify.display.mdAndDown"
        v-model="filtersMenu"
        location="end"
        :close-on-content-click="false"
      >
        <template #activator="{ props }">
          <v-btn v-bind="props" icon>
            <v-icon>mdi-filter</v-icon>
          </v-btn>
        </template>
        <v-card flat>
          <v-card-title class="py-1">
            <span>Critical Dates Filters</span>
            <v-spacer />
            <v-btn icon @click="filtersMenu = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
        </v-card>
        <v-divider />
        <v-card class="pa-2 d-flex gap-2 justify-center flex-column" flat>
          <OpenInvoicesDateFilter
            v-model:date-start="date_start"
            v-model:date-end="date_end"
            @fetch="fetchWrapper"
          />
          <v-select
            v-if="isAdmin"
            label="Select Agent"
            hide-details
            :items="members"
            item-title="full_name"
            item-value="id"
            clearable
            @update:model-value="changeUser"
          />
          <v-switch
            v-model="showOnlyFutureDates"
            class="mt-0"
            label="Show only future dates"
            hide-details
          />
          <v-btn color="primary" @click="filtersMenu = false">Apply</v-btn>
        </v-card>
      </v-menu>
      <div v-else class="d-flex align-center gap-2">
        <OpenInvoicesDateFilter
          v-model:date-start="date_start"
          v-model:date-end="date_end"
          @fetch="fetchWrapper"
        />
        <v-select
          v-if="isAdmin"
          label="Select Agent"
          hide-details
          :items="members"
          item-title="full_name"
          item-value="id"
          style="max-width: 200px"
          clearable
          @update:model-value="changeUser"
        />
        <v-switch
          v-model="showOnlyFutureDates"
          class="mt-0"
          label="Show only future dates"
          hide-details
        />
      </div>
    </template>
    <template #content>
      <v-card class="pt-0" flat>
        <v-card-text class="pt-0 px-0">
          <v-data-table-server
            class="sc-data-table sc-data-table--fixed-per-page elevation-0 mb-0 dashboard-table"
            item-value="_id"
            :items="items"
            :headers="headers"
            :mobile-breakpoint="0"
            :page="tableOptions.page"
            :hide-default-header="$vuetify.display.mdAndDown"
            :items-per-page="tableOptions.itemsPerPage"
            :items-per-page-options="[]"
            :items-length="totalRows"
            @update:page="updateTableOptions({ page: $event })"
          >
            <template #header.multi="{ column: header }">
              <template
                v-for="item in header.multi"
                :key="item.text"
              >
                <BaseTableHeaderMulti
                  :header="item"
                  :is-sort-descending="isSortDescending"
                  :is-active="isActiveSort(item)"
                  :color="isActiveSort(item) ? 'black' : 'grey'"
                  @sortChangeEvent="sortChangeHandler"
                />
              </template>
            </template>
            <template #item="{ item, index }">
              <CriticalDateMobileCard
                v-if="$vuetify.display.mdAndDown"
                :item="item"
                :active-account-id="activeAccountId"
                :class="index === 0 ? 'mt-0' : 'mt-2'"
              />
              <tr v-else>
                <td class="sc-data-list">
                  <router-link
                    class="subtitle-1 text-black"
                    :to="getAccountRoute({ name: 'occupier', params: { id: item.lease_date.occupier._key } })"
                    target="_blank"
                  >
                    {{ item.lease_date.occupier.name }}
                  </router-link>
                </td>
                <td class="sc-data-list-subject">
                  {{ mapCriticalDateTypeToTitle(item.type) }}
                </td>
                <td class="sc-data-list">
                  {{ item.lease_date.type_name }}
                </td>
                <td class="sc-data-list">
                  {{ $filters.formatJSDate(item.date) }}
                </td>
                <td class="sc-data-list">
                  <div v-for="notification in getNotificationItems(item)" :key="notification.key">
                    <v-avatar :color="notification.isDateInFuture ? 'orange' : 'red'" size="12" /> {{ $filters.formatDate(notification.date) }}
                  </div>
                </td>
                <td class="sc-data-list">
                  <div v-html="item.lease_date.description" />
                </td>
              </tr>
            </template>
          </v-data-table-server>
        </v-card-text>
      </v-card>
    </template>
  </DashboardCard>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapActions, mapMutations } from 'vuex';
import { concat } from 'lodash-es';
import { DateTime } from 'luxon';
import { v4 as uuid } from 'uuid';

import { PaginationMixin } from '@/bundles/BaseTable/mixins/PaginationMixin';
import { NotificationMutations } from '@/store/types/mutation-types';
import { criticalDateService } from '@/bundles/CriticalDates/factory/criticalDateServiceFactory';
import CriticalDateListResponse from '@/bundles/CriticalDates/models/CriticalDateListResponse';
import { UserWidgetsEnum } from '@/bundles/Settings/enums/userLayoutEnum';
import { criticalDatesHeadersData, mapCriticalDateTypeToTitle } from '@/bundles/CriticalDates/helpers';
import { IHeader, ITableOptions } from '@/bundles/BaseTable/interfaces';
import { Nullable } from '@/utils/types';
import Member from '@/bundles/Members/models/Member';
import { StoreModulesNamesEnum } from '@/store/StoreModulesNamesEnum';
import { LOAD_ACTIVE_MEMBERS } from '@/store/modules/members/members-types';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { useUserRoles } from '@/bundles/App/composables/useUserRoles';

import DashboardCard from '@/bundles/Dashboard/components/DashboardCard.vue';
import BaseTableHeaderMulti from '@/bundles/BaseTable/components/BaseTableHeaderMulti.vue';
import OpenInvoicesDateFilter from '@/bundles/Dashboard/components/filters/OpenInvoicesDateFilter.vue';
import CriticalDateMobileCard from '@/bundles/CriticalDates/components/CriticalDateMobileCard.vue';

type MemberWithFullName = Member & { full_name: string };

const WIDGET_ITEMS_PER_PAGE = 10;

interface ICriticalDateWidgetInstance {
  loading: boolean;
  items: CriticalDateListResponse[];
  totalRows: number;
  showOnlyFutureDates: boolean;
  tableOptionsType: 'criticalDatesDashboardWidget';
  date_start: Nullable<string>;
  date_end: Nullable<string>;
  memberId: Nullable<string>;
  members: MemberWithFullName[];
  filtersMenu: boolean;
}

export default defineComponent({
  name: 'CriticalDatesDashboardWidget',

  components: {
    CriticalDateMobileCard,
    OpenInvoicesDateFilter,
    DashboardCard,
    BaseTableHeaderMulti
  },
  mixins: [PaginationMixin, AccountLayoutMixin],

  props: {
    expanded: Boolean,
  },

  emits: ['update:expanded'],

  setup () {
    const { isAdmin } = useUserRoles();

    return { isAdmin };
  },

  data: (): ICriticalDateWidgetInstance => ({
    loading: false,
    items: [],
    totalRows: 0,
    showOnlyFutureDates: false,
    tableOptionsType: 'criticalDatesDashboardWidget',
    date_start: null,
    date_end: null,
    memberId: null,
    members: [],
    filtersMenu: false,
  }),

  computed: {
    widgetType (): UserWidgetsEnum.critical_dates {
      return UserWidgetsEnum.critical_dates;
    },
    headers (): IHeader[] {
      return criticalDatesHeadersData
    },
    isSortDescending () {
      return !!this.tableOptions.sortDesc?.[0];
    }
  },

  watch: {
    tableOptions: {
      handler: function () {
        this.fetchWrapper();
      },
      deep: true,
      immediate: true,
    },
    showOnlyFutureDates: function () {
      this.resetPagination(this.tableOptionsType);
    }
  },

  created () {
    if (this.tableOptions.itemsPerPage !== WIDGET_ITEMS_PER_PAGE) {
      this.updateTableOptions({ itemsPerPage: WIDGET_ITEMS_PER_PAGE });
    }

    if (this.isAdmin) {
      this.getMembers();
    }
  },

  methods: {
    mapCriticalDateTypeToTitle,
    ...mapMutations('Notifications', {
      addNotification: NotificationMutations.ADD_NOTIFICATION,
    }),
    ...mapActions(StoreModulesNamesEnum.Member, {
      loadActiveMembers: LOAD_ACTIVE_MEMBERS,
    }),
    updateTableOptions (tableOptions: Partial<ITableOptions>) {
      this.setTableOptions({
        type: this.tableOptionsType,
        tableOptions: { ...this.tableOptions, ...tableOptions },
      });
    },
    fetchWrapper: async function () {
      this.loading = true;
      try {
        const params = {
          take: this.tableOptions.itemsPerPage,
          skip: this.pageOffset,
          sort: this.tableOptions.sortBy?.[0],
          descending: this.tableOptions.sortDesc?.[0],
          show_only_future_dates: this.showOnlyFutureDates,
        };

        if (this.date_start) {
          Object.assign(params, { date_start: this.date_start });
        }
        if (this.date_end) {
          Object.assign(params, { date_stop: this.date_end });
        }
        // @ts-ignore
        if (this.isAdmin && this.memberId) {
          Object.assign(params, { member: this.memberId });
        }

        const { data, total } = await criticalDateService.list(params);
        this.items = data;
        this.totalRows = total;
        const wrapper = document.querySelector('.critical-date-container');
        if (wrapper) {
          wrapper.scrollTop = 0;
        }
      } catch (error: any) {
        this.addNotification({ ...error });
      } finally {
        this.loading = false;
      }
    },
    isActiveSort: function (item: IHeader): boolean {
      const sortBy = this.tableOptions?.sortBy || [];
      const valueText = item.value;

      return sortBy[0] === valueText;
    },
    sortChangeHandler: function (payload: { sortBy: string[]; sortDesc: boolean[] }) {
      this.setFilterSort('criticalDatesDashboardWidget', payload);
    },
    async getMembers () {
      const data = await this.loadActiveMembers();

      this.members = data.map((item) => {
        return {
          ...item,
          full_name: `${item.profile.first_name} ${item.profile.last_name}`
        }
      });
    },
    changeUser (value: string) {
      this.memberId = value;
      this.fetchWrapper();
    },
    getNotificationItems (criticalDate) {
      const inside_reminders = criticalDate.lease_date.inside_notification.reminders || [];
      const outside_reminders = criticalDate.lease_date.outside_notification.reminders || [];

      const reminders = concat(inside_reminders, outside_reminders);

      return reminders.filter(reminder => !!reminder.prior_to.toCompare).map(reminder => {
        const date = DateTime.fromISO(criticalDate.date).minus(reminder.prior_to.toCompare).valueOf();

        return {
          date,
          isDateInFuture: date > Date.now(),
          key: uuid()
        }
      });
    }
  },
});
</script>

<style lang="scss" scoped>
.critical-date-container {
  max-height: 450px;
  overflow: auto;
  padding: 0 10px;
  width: calc(100% - 20px);
}

:deep(.dashboard-table) {
  .v-data-table-footer__info {
    margin-left: auto;
  }
}
</style>
